/**
 * @flow
 * */
import NetworkService from 'core/services/network/NetworkService';
import BaseSDK from 'core/sdk/BaseSDK';
import config from 'config';

/**
 * Risk Weekly Report API client.
 *
 * Both endpoints are POST with a body, matching the house style for filtered reads
 * (see GRMTReportController). Calls are abortable so switching the week range mid-flight
 * cannot let a stale response overwrite a newer one.
 *
 * @class RiskWeeklyReportSDK
 */
class RiskWeeklyReportSDK extends BaseSDK {
    _rmtApiService: NetworkService;

    constructor() {
      super();
      this._rmtApiService = new NetworkService(config.rmtApi, []);
    }

    /**
     * getWeeks — the week picker's payload: { weeks, ready, message, warehouse }.
     *
     * An object rather than the bare array it used to be, because an empty list has two very
     * different meanings: a store with no completed week, and a store still being built. The
     * second carries the arrival time, and the page is expected to show it rather than ask for
     * a report that is not there yet.
     *
     * @param {Object} filters - brand / range filter
     * @returns {Promise<Object>} -
     */
    async getWeeks(filters: Object = {}): Promise<Object> {
      const { body } = await this._rmtApiService.makeAPIPostRequest(
        'RiskWeeklyReport/GetWeeks',
        { body: filters, abortController: this.setAbortController('getWeeks') }
      );
      // ResponseModelFilter wraps every action result in ResponseModel { Data }.
      // Returning `body` would hand the envelope to the store; the weeks array lives under Data.
      return body.Data;
    }

    /**
     * getOverview — Live Dashboard payload: integer components per week and per department.
     * @param {Object} filters - brand / range filter
     * @returns {Promise<Object>} -
     */
    async getOverview(filters: Object = {}): Promise<Object> {
      const { body } = await this._rmtApiService.makeAPIPostRequest(
        'RiskWeeklyReport/GetOverview',
        { body: filters, abortController: this.setAbortController('getOverview') }
      );
      // See getWeeks: the overview viewmodel is under the ResponseModel envelope's Data.
      return body.Data;
    }

    /**
     * getDepartmentDetail — alert-level drilldown for one department.
     * @param {Object} filters - must carry brand and department
     * @returns {Promise<Object>} -
     */
    async getDepartmentDetail(filters: Object = {}): Promise<Object> {
      // Named per department: several drilldowns can be open, and one shared controller would
      // make only the most recent request cancellable.
      const name = `getDepartmentDetail:${filters.brand || '-'}:${filters.department || '-'}`;
      const { body } = await this._rmtApiService.makeAPIPostRequest(
        'RiskWeeklyReport/GetDepartmentDetail',
        { body: filters, abortController: this.setAbortController(name) }
      );
      // See getWeeks: the viewmodel is under the ResponseModel envelope's Data.
      return body.Data;
    }

    /**
     * getPartnerCoverage — is the partner list complete, checked against the platform.
     *
     * The only call here that makes the server read outside the report's own partner scope, so
     * it is never issued on page load: the panel fetches when a reader opens it. One week.
     *
     * @param {Object} filters - week names the reporting week; omitted means the latest complete
     * @returns {Promise<Object>} -
     */
    async getPartnerCoverage(filters: Object = {}): Promise<Object> {
      const { body } = await this._rmtApiService.makeAPIPostRequest(
        'RiskWeeklyReport/GetPartnerCoverage',
        { body: filters, abortController: this.setAbortController('getPartnerCoverage') }
      );
      // See getWeeks: the viewmodel is under the ResponseModel envelope's Data.
      return body.Data;
    }

    // =================================================================================
    // DISABLED - endpoints for the five views the first production release does not ship.
    // The server side is commented out too, so these would 404.
    // RMT8993, 2026-09-14; see docs/report_docs/13-live-dashboard-only.md.
    // =================================================================================
    // /**
     // * getGrmtUnderTest - settings on trial, their review rates and test deadlines.
     // * @param {Object} filters - brand / range filter
     // * @returns {Promise<Object>} -
     // */
    // async getGrmtUnderTest(filters: Object = {}): Promise<Object> {
      // const { body } = await this._rmtApiService.makeAPIPostRequest(
        // 'RiskWeeklyReport/GetGrmtUnderTest',
        // { body: filters, abortController: this.setAbortController('getGrmtUnderTest') }
      // );
      // // See getWeeks: the viewmodel is under the ResponseModel envelope's Data.
      // return body.Data;
    // }

    // /**
     // * getGrmtSettingDetail - one setting under test: status split, weekly shape and its alerts.
     // * @param {Object} filters - must carry brand and settingName; reportRow and statusFilter optional
     // * @returns {Promise<Object>} -
     // */
    // async getGrmtSettingDetail(filters: Object = {}): Promise<Object> {
      // // Named per setting for the same reason as getDepartmentDetail: two brands can each have a
      // // row open, and a shared controller would let one cancel the other.
      // // The status filter is deliberately not part of the name: changing it supersedes the request
      // // for the same row, so letting it cancel the previous one is the wanted behaviour.
      // const name = [
        // 'getGrmtSettingDetail',
        // filters.brand || '-',
        // filters.settingName || '-',
        // filters.reportRow || '-'
      // ].join(':');
      // const { body } = await this._rmtApiService.makeAPIPostRequest(
        // 'RiskWeeklyReport/GetGrmtSettingDetail',
        // { body: filters, abortController: this.setAbortController(name) }
      // );
      // // See getWeeks: the viewmodel is under the ResponseModel envelope's Data.
      // return body.Data;
    // }

    // /**
     // * getEmployeeAnalytics - per-analyst and per-department load for the window.
     // * @param {Object} filters - brand / range filter
     // * @returns {Promise<Object>} -
     // */
    // async getEmployeeAnalytics(filters: Object = {}): Promise<Object> {
      // const { body } = await this._rmtApiService.makeAPIPostRequest(
        // 'RiskWeeklyReport/GetEmployeeAnalytics',
        // { body: filters, abortController: this.setAbortController('getEmployeeAnalytics') }
      // );
      // // See getWeeks: the viewmodel is under the ResponseModel envelope's Data.
      // return body.Data;
    // }

    // /**
     // * getEmployeeDetail - one analyst's weekly load, SLA outcome, priority mix and outcome.
     // * @param {Object} filters - must carry brand and employeeId
     // * @returns {Promise<Object>} -
     // */
    // async getEmployeeDetail(filters: Object = {}): Promise<Object> {
      // // Named per analyst: each brand's table can have a row open, and a shared controller
      // // would let one cancel the other.
      // const name = `getEmployeeDetail:${filters.brand || '-'}:${filters.employeeId || '-'}`;
      // const { body } = await this._rmtApiService.makeAPIPostRequest(
        // 'RiskWeeklyReport/GetEmployeeDetail',
        // { body: filters, abortController: this.setAbortController(name) }
      // );
      // // See getWeeks: the viewmodel is under the ResponseModel envelope's Data.
      // return body.Data;
    // }

    // /**
     // * getPartnerTrigger - per-partner or per-trigger components within each routed department.
     // * @param {Object} filters - brand / range filter plus grain
     // * @returns {Promise<Object>} -
     // */
    // async getPartnerTrigger(filters: Object = {}): Promise<Object> {
      // // Named per grain: partner and trigger are separate requests and one must not cancel the
      // // other's response when the operator flips the switch twice.
      // const name = `getPartnerTrigger:${filters.grain || 'partner'}`;
      // const { body } = await this._rmtApiService.makeAPIPostRequest(
        // 'RiskWeeklyReport/GetPartnerTrigger',
        // { body: filters, abortController: this.setAbortController(name) }
      // );
      // // See getWeeks: the viewmodel is under the ResponseModel envelope's Data.
      // return body.Data;
    // }

    /**
     * getCalendar - what the fact store holds, day by day, plus the weeks those days fall into.
     *
     * Takes no filter: coverage is a property of the store, not of a brand or a range. It is what
     * bounds the date picker, so a reader cannot select a fortnight that was never extracted and
     * read the resulting emptiness as a quiet fortnight.
     *
     * @returns {Promise<Object>} -
     */
    async getCalendar(): Promise<Object> {
      const { body } = await this._rmtApiService.makeAPIPostRequest(
        'RiskWeeklyReport/GetCalendar',
        { abortController: this.setAbortController('getCalendar') }
      );
      // See getWeeks: the viewmodel is under the ResponseModel envelope's Data.
      return body.Data;
    }

    /**
     * getDailyStatistics - every count about the alerts of one day.
     * @param {Object} filters - { day: yyyy-MM-dd or null for the latest, brand }
     * @returns {Promise<Object>} -
     */
    async getDailyStatistics(filters: Object = {}): Promise<Object> {
      // One controller for the tab: a newer day supersedes the one still loading.
      const { body } = await this._rmtApiService.makeAPIPostRequest(
        'RiskWeeklyReport/GetDailyStatistics',
        { body: filters, abortController: this.setAbortController('getDailyStatistics') }
      );
      // See getWeeks: the viewmodel is under the ResponseModel envelope's Data.
      return body.Data;
    }

    /**
     * getRangeStatistics - the exported report's statistics over the dashboard's window, per brand.
     * @param {Object} filters - { brand, weekFrom, weekTo }, as for getOverview
     * @returns {Promise<Object>} -
     */
    async getRangeStatistics(filters: Object = {}): Promise<Object> {
      const { body } = await this._rmtApiService.makeAPIPostRequest(
        'RiskWeeklyReport/GetRangeStatistics',
        { body: filters, abortController: this.setAbortController('getRangeStatistics') }
      );
      // See getWeeks: the viewmodel is under the ResponseModel envelope's Data.
      return body.Data;
    }

    /**
     * getExploreCube - the Explore metrics cube at the requested grain.
     * @param {Object} filters - brand / range filter plus grain
     * @returns {Promise<Object>} -
     */
    async getExploreCube(filters: Object = {}): Promise<Object> {
      // Named per grain, as getPartnerTrigger is: the three grains are independent requests and
      // flipping between them must not cancel a response that is still wanted.
      const name = `getExploreCube:${filters.grain || 'department'}`;
      const { body } = await this._rmtApiService.makeAPIPostRequest(
        'RiskWeeklyReport/GetExploreCube',
        { body: filters, abortController: this.setAbortController(name) }
      );
      // See getWeeks: the viewmodel is under the ResponseModel envelope's Data.
      return body.Data;
    }

    // /**
     // * getInsights - ranked insights for one reporting week.
     // * @param {Object} filters - optional { week }
     // * @returns {Promise<Object>} -
     // */
    // async getInsights(filters: Object = {}): Promise<Object> {
      // const { body } = await this._rmtApiService.makeAPIPostRequest(
        // 'RiskWeeklyReport/GetInsights',
        // { body: filters, abortController: this.setAbortController('getInsights') }
      // );
      // // See getWeeks: the viewmodel is under the ResponseModel envelope's Data.
      // return body.Data;
    // }

    /**
     * previewLane — what a candidate export lane would collect over one week.
     *
     * Not abortable, like clearCache and unlike the reads: a preview is something the operator
     * asked for by pressing a button, and cancelling it when they edit the next field would
     * leave them looking at a number from the previous candidate.
     *
     * @param {Object} lane - the candidate, whole: { saved_filter, week, source_report, brand,
     *   sub_tag, partner_ids, partner_tags, partner_tag, alert_statuses, trigger_ids,
     *   players_count, grmt_group_id }
     * @returns {Promise<Object>} - { current, candidate, row_delta, elapsed_ms, notes }
     */
    async previewLane(lane: Object): Promise<Object> {
      const { body } = await this._rmtApiService.makeAPIPostRequest(
        'RiskWeeklyReport/PreviewLane',
        { body: lane }
      );
      // See getWeeks: the viewmodel is under the ResponseModel envelope's Data.
      return body.Data;
    }

    /**
     * clearCache — drops the backend's report caches.
     *
     * Deliberately NOT abortable, unlike every read here. The others are cancelled when a newer
     * request supersedes them; this one changes server state, so abandoning it mid-flight would
     * leave the operator unsure whether it happened.
     *
     * @returns {Promise<Object>} - what was cleared
     */
    async clearCache(): Promise<Object> {
      const { body } = await this._rmtApiService.makeAPIPostRequest(
        'RiskWeeklyReport/ClearCache',
        { body: {} }
      );
      // See getWeeks: the viewmodel is under the ResponseModel envelope's Data.
      return body.Data;
    }

    /**
     * cancelCalls
     * @param {Object} params - { name }
     * @returns {void} -
     */
    async cancelCalls(params: { name: string | Array<string> }): Promise<void> {
      this.abortCall(params.name);
    }
}

export default new RiskWeeklyReportSDK();
